/**
 * In-flight request deduplication for GET requests with network retry.
 *
 * When multiple components dispatch the same GET request before the first
 * one resolves, this utility ensures only ONE network call is made. All
 * callers receive the same Promise (and therefore the same resolved value).
 *
 * Mutations (POST/PATCH/DELETE) are never deduplicated.
 *
 * Network errors (connection lost, DNS failure) are retried up to
 * MAX_RETRIES times with exponential backoff + jitter. Server errors (4xx/5xx)
 * are NOT retried — those are application-level decisions.
 *
 * Lifecycle:
 *   1. generateKey() produces a stable string from method + url + params
 *   2. deduplicateRequest() checks the pendingRequests Map
 *      - Miss → execute fetch, store Promise in Map, return it
 *      - Hit  → return the stored Promise directly (no fetch)
 *   3. On resolve or reject → entry is removed from Map via `finally`
 */

const pendingRequests = new Map<string, Promise<unknown>>();

const MAX_RETRIES = 2; // up to 3 total attempts (1 initial + 2 retries)
const BASE_DELAY_MS = 500;
const MAX_JITTER_MS = 300;

/**
 * Determine if an error is a network-level error that warrants a retry.
 *
 * Network errors: connection dropped, DNS failure.
 * Server errors (4xx/5xx): the platform client resolves fetch and rejects
 * with a parsed JSON body (plain object/string) — those are NOT retried.
 *
 * AbortError is intentionally excluded: it originates from AbortController.abort()
 * (component unmount, user navigation) and retrying would be counterproductive.
 */
export const isNetworkError = (error: unknown): boolean => {
  // The Fetch API rejects with TypeError on network failures, but the message
  // varies by browser engine. We check known patterns to avoid retrying
  // unrelated TypeErrors (e.g. accessing a property on undefined).
  //   - Chrome/Edge: "Failed to fetch"
  //   - Firefox:     "NetworkError when attempting to fetch resource"
  //   - Safari:      "Load failed"
  if (error instanceof TypeError) {
    const msg = error.message.toLowerCase();
    return (
      msg.includes("fetch") ||
      msg.includes("network") ||
      msg.includes("load failed")
    );
  }

  return false;
};

/**
 * Calculate retry delay with exponential backoff + random jitter.
 *
 * attempt 0 → ~500-800ms
 * attempt 1 → ~1000-1300ms
 */
export const getRetryDelay = (attempt: number): number => {
  const exponentialDelay = BASE_DELAY_MS * 2 ** attempt;
  const jitter = Math.random() * MAX_JITTER_MS;
  return exponentialDelay + jitter;
};

let waitFn = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

/**
 * Generate a stable deduplication key for a request.
 *
 * @returns A non-empty string for deduplicable requests, or `""` to skip dedup.
 */
export const generateRequestKey = (
  method: string,
  url: string,
  data?: object | FormData
): string => {
  // Only deduplicate GET requests — mutations must always execute
  if (method !== "GET") return "";

  // FormData cannot be serialized — skip dedup (extremely rare for GET)
  if (data instanceof FormData) return "";

  let key = `GET:${url}`;

  // Include query params in the key for differentiation
  if (data !== undefined && data !== null && Object.keys(data).length > 0) {
    const sortedKeys = Object.keys(data).sort();
    const stable: Record<string, unknown> = {};
    for (const k of sortedKeys) {
      stable[k] = (data as Record<string, unknown>)[k];
    }
    key += `:${JSON.stringify(stable)}`;
  }

  return key;
};

/**
 * Execute a request with retry logic for network errors.
 *
 * Retries up to MAX_RETRIES times with exponential backoff + jitter
 * for network-level errors only. Server errors are propagated immediately.
 */
const executeWithRetry = async <T>(execute: () => Promise<T>): Promise<T> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await execute();
    } catch (error) {
      if (attempt < MAX_RETRIES && isNetworkError(error)) {
        await waitFn(getRetryDelay(attempt));
        continue;
      }
      throw error;
    }
  }
};

/**
 * Execute a request with in-flight deduplication and network retry.
 *
 * If `key` is empty (mutation or unencodable request), the request
 * executes immediately with no deduplication.
 *
 * If `key` matches an in-flight request, the existing Promise is returned.
 *
 * @param key     - Deduplication key from `generateRequestKey`
 * @param execute - Factory that performs the actual HTTP request
 * @param options - `skipDeduplication` forces a separate network call; see
 *                  `apiProxyRequestFresh`
 * @returns       - The (shared or new) Promise
 */
export const deduplicateRequest = <T>(
  key: string,
  execute: () => Promise<T>,
  { skipDeduplication = false }: { skipDeduplication?: boolean } = {}
): Promise<T> => {
  // No dedup for mutations or unencodable requests
  if (key === "") return execute();

  /*
   * Sharing an in-flight Promise assumes both callers want the same answer.
   * A caller that knows the resource just changed does not: it would get the
   * response to a request issued before the change. Keep the retry behaviour,
   * skip the map entirely — registering under the same key would also let the
   * next caller piggyback on this one.
   */
  if (skipDeduplication) return executeWithRetry(execute);

  // Return existing in-flight Promise if one exists for this key
  const pending = pendingRequests.get(key);
  if (pending) return pending as Promise<T>;

  const promise = executeWithRetry(execute).finally(() => {
    pendingRequests.delete(key);
  });

  pendingRequests.set(key, promise);
  return promise;
};

// ── Test-only exports ──────────────────────────────────────────────────────

/**
 * Returns the current number of in-flight deduplicated requests.
 * Exposed for unit tests only.
 */
export const _getPendingCount = (): number => pendingRequests.size;

/**
 * Clears all pending entries. Call in `afterEach` to prevent test leakage.
 */
export const _clearPending = (): void => {
  pendingRequests.clear();
};

/**
 * Override the internal wait function. Use `() => Promise.resolve()` in tests
 * to eliminate retry delays.
 */
export const _setWaitFn = (fn: (ms: number) => Promise<void>): void => {
  waitFn = fn;
};
